import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ExternalLink,
  FlaskConical,
  Instagram,
  RefreshCw,
  Shield,
  UserRoundSearch,
} from "lucide-react";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";
import { formatApiError } from "../lib/api";
import { signInWithGoogle, signInWithApple, signOutSupabase, formatAuthError } from "../lib/supabaseAuth";
import SocialAuthButtons from "../components/SocialAuthButtons";
import { StickerButton, StickerCard } from "../components/Sticker";
import { Button, Card, CardBody } from "../components/ui";
import { adminGet, adminPatch, adminPost, organizerInstagramUrl } from "../lib/adminApi";
import { instagramHref } from "../lib/instagram";
import SwitchUserModal from "../components/admin/SwitchUserModal";

function formatWhen(iso) {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function KpiTile({ label, value }) {
  return (
    <div className="rounded-btn border border-black/[0.08] bg-white px-4 py-3 min-w-[120px]">
      <p className="font-ui font-medium text-[11px] uppercase tracking-wide text-ink/55">{label}</p>
      <p className="font-ui font-semibold text-[22px] tracking-tight mt-0.5 tabular-nums">{value ?? "—"}</p>
    </div>
  );
}

function publicUrl(path) {
  if (!path) return null;
  if (typeof window === "undefined") return path;
  return `${window.location.origin}${path.startsWith("/") ? path : `/${path}`}`;
}

export default function AdminPage() {
  const [authUser, setAuthUser] = useState(undefined);
  const [adminEmail, setAdminEmail] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [organizers, setOrganizers] = useState([]);
  const [verifiedHosts, setVerifiedHosts] = useState([]);
  const [acting, setActing] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [hostQuery, setHostQuery] = useState("");
  const [switchOpen, setSwitchOpen] = useState(false);
  const [verificationOpen, setVerificationOpen] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setAuthUser(session?.user || null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const verifyAdmin = useCallback(async () => {
    if (!authUser) {
      setAdminEmail(null);
      setAuthError(null);
      return;
    }
    try {
      const data = await adminGet("/me");
      setAdminEmail(data.email);
      setAuthError(null);
    } catch (err) {
      setAdminEmail(null);
      setAuthError(formatApiError(err));
    }
  }, [authUser]);

  useEffect(() => {
    verifyAdmin();
  }, [verifyAdmin]);

  const loadOrganizers = useCallback(async () => {
    const [all, verified] = await Promise.all([
      adminGet("/organizers"),
      adminGet("/organizers?verified_host=1"),
    ]);
    setOrganizers(all.items || []);
    setVerifiedHosts(verified.items || []);
  }, []);

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    try {
      const data = await adminGet("/dashboard");
      setKpi(data.kpi || null);
      setAccounts(data.accounts || []);
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!adminEmail) return;
    loadOrganizers().catch((err) => toast.error(formatApiError(err)));
    loadDashboard().catch((err) => toast.error(formatApiError(err)));
  }, [adminEmail, loadOrganizers, loadDashboard]);

  async function handleGoogleSignIn() {
    setLoading(true);
    try {
      await signInWithGoogle("/app-admin");
    } catch (err) {
      const msg = formatAuthError(err);
      if (msg !== "Sign-in cancelled.") toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleAppleSignIn() {
    setLoading(true);
    try {
      await signInWithApple("/app-admin");
    } catch (err) {
      const msg = formatAuthError(err);
      if (msg !== "Sign-in cancelled.") toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleSignOut() {
    await signOutSupabase();
    setAdminEmail(null);
  }

  async function handleRefresh() {
    setRefreshing(true);
    try {
      await adminPost("/dashboard/refresh");
      await loadDashboard();
      toast.success("Dashboard refreshed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRefreshing(false);
    }
  }

  async function updateAccount(orgId, patch) {
    setActing(orgId);
    try {
      await adminPatch(`/organizers/${orgId}`, patch);
      toast.success("Account updated");
      await loadOrganizers();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActing(null);
    }
  }

  const filteredHosts = useMemo(() => {
    const q = hostQuery.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((row) => {
      const hay = [
        row.name,
        row.email,
        row.personal_instagram,
        row.school_instagram,
        ...(row.school_slugs || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [accounts, hostQuery]);

  if (authUser === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="font-display font-bold text-[18px]">Loading…</p>
      </div>
    );
  }

  if (!authUser) {
    return (
      <div className="min-h-screen bg-canvas font-body text-ink pb-20">
        <div className="max-w-page mx-auto px-4 pt-8">
          <Link to="/" className="inline-flex items-center gap-1.5 rounded-btn border border-black/[0.08] bg-white px-3 py-1.5 min-h-[44px] font-ui font-medium text-[13px] text-ink hover:bg-canvas transition-colors">
            <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Home
          </Link>
          <div className="mt-8">
            <p className="font-ui font-medium text-[12px] uppercase tracking-wide text-ink/60 flex items-center gap-1">
              <Shield className="w-4 h-4" /> Admin
            </p>
            <h1 className="font-ui font-semibold text-[28px] sm:text-[32px] mt-1 tracking-tight">Sign in to review</h1>
            <p className="mt-2 font-normal text-[14px] text-ink/65">Supabase account must be on the admin allowlist.</p>
          </div>
          <div className="mt-6">
            <SocialAuthButtons loading={loading} onGoogle={handleGoogleSignIn} onApple={handleAppleSignIn} />
          </div>
        </div>
      </div>
    );
  }

  if (!adminEmail) {
    return (
      <div className="min-h-screen bg-canvas font-body text-ink pb-20">
        <div className="max-w-page mx-auto px-4 pt-8">
          <Link to="/" className="inline-flex items-center gap-1.5 rounded-btn border border-black/[0.08] bg-white px-3 py-1.5 min-h-[44px] font-ui font-medium text-[13px] text-ink hover:bg-canvas transition-colors">
            <ArrowLeft className="w-4 h-4" strokeWidth={3} /> Home
          </Link>
          <StickerCard className="p-6 space-y-3 mt-6">
            <p className="font-ui font-semibold text-[20px] sm:text-[22px]">Not authorized</p>
            <p className="font-semibold text-ink/80">
              {authError || "This Google account is not on the admin allowlist."}
            </p>
            <p className="text-[13px] font-semibold text-ink/60">Signed in as {authUser.email}</p>
            <p className="text-[13px] font-semibold text-ink/70 leading-relaxed">
              Sign out and try a different account, or email{" "}
              <a
                href="mailto:flipflopapps@gmail.com"
                className="text-plum underline underline-offset-2"
              >
                flipflopapps@gmail.com
              </a>{" "}
              if this keeps happening.
            </p>
            <StickerButton color="white" onClick={handleSignOut}>Sign out</StickerButton>
          </StickerCard>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas font-body text-ink pb-20">
      <header className="max-w-page mx-auto px-4 pt-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="font-ui font-medium text-[12px] uppercase tracking-wide text-ink/60 flex items-center gap-1">
            <FlaskConical className="w-3.5 h-3.5" /> Admin
          </p>
          <h1 className="font-ui font-semibold text-[24px] tracking-tight">Ops dashboard</h1>
          <p className="text-[13px] font-medium text-ink/55">{adminEmail}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setSwitchOpen(true)}
            data-testid="open-switch-user"
          >
            <UserRoundSearch className="w-4 h-4" />
            Switch user
          </Button>
          <Link to="/events" className="inline-flex items-center rounded-btn border border-black/[0.08] bg-white px-3 py-2 min-h-[44px] font-ui font-medium text-[13px] text-ink hover:bg-canvas transition-colors">
            App
          </Link>
          <StickerButton color="white" size="sm" onClick={handleSignOut}>Sign out</StickerButton>
        </div>
      </header>

      <div className="max-w-page mx-auto px-4 mt-6 space-y-8">
        <section className="space-y-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-ui font-semibold text-[18px]">Platform</h2>
              <p className="text-[13px] text-ink/55">
                Updated {formatWhen(kpi?.refreshed_at)}
                {dashboardLoading ? " · Loading…" : ""}
              </p>
            </div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleRefresh}
              disabled={refreshing}
              data-testid="refresh-dashboard"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Refreshing…" : "Refresh now"}
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <KpiTile label="Hosts" value={kpi?.hosts_count} />
            <KpiTile label="Non-hosts" value={kpi?.non_hosts_count} />
            <KpiTile label="Schools" value={kpi?.schools_count} />
            <KpiTile label="Events" value={kpi?.events_count} />
          </div>
          <div className="flex flex-wrap gap-2">
            <KpiTile label="Signups · week" value={kpi?.signups_week} />
            <KpiTile label="Signups · month" value={kpi?.signups_month} />
            <KpiTile label="Signups · all" value={kpi?.signups_all} />
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-ui font-semibold text-[18px]">Hosts</h2>
              <p className="text-[13px] text-ink/55">
                Public links, engagement, and contact details (test data excluded).
              </p>
            </div>
            <input
              type="search"
              value={hostQuery}
              onChange={(e) => setHostQuery(e.target.value)}
              placeholder="Search name, email, IG…"
              className="field max-w-[260px]"
              data-testid="host-directory-search"
            />
          </div>

          {filteredHosts.length === 0 ? (
            <Card>
              <CardBody className="p-5">
                <p className="font-ui font-semibold text-[16px]">
                  {accounts.length === 0
                    ? "No snapshot yet — hit Refresh now."
                    : "No hosts match that search."}
                </p>
              </CardBody>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredHosts.map((row) => {
                const ig = instagramHref(row.personal_instagram || row.school_instagram || "");
                const hostHref = publicUrl(row.host_path);
                return (
                  <Card key={row.organizer_id} data-testid={`host-row-${row.organizer_id}`}>
                    <CardBody className="p-4 sm:p-5 space-y-3">
                      <div className="flex flex-wrap justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="font-ui font-semibold text-[16px] truncate">
                            {row.name || row.organizer_id}
                          </h3>
                          <p className="text-[13px] text-ink/55 break-all">{row.email || "—"}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {hostHref && (
                            <a
                              href={hostHref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 rounded-btn border border-black/[0.08] bg-white px-3 py-1.5 min-h-[44px] font-ui font-medium text-[13px] text-ink hover:bg-canvas transition-colors"
                            >
                              <ExternalLink className="w-4 h-4" /> Host page
                            </a>
                          )}
                          {ig && (
                            <a
                              href={ig}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 rounded-btn border border-black/[0.08] bg-white px-3 py-1.5 min-h-[44px] font-ui font-medium text-[13px] text-ink hover:bg-canvas transition-colors"
                            >
                              <Instagram className="w-4 h-4" /> IG
                            </a>
                          )}
                        </div>
                      </div>

                      {(row.school_slugs || []).length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {row.school_slugs.map((slug) => (
                            <a
                              key={slug}
                              href={publicUrl(`/school/${slug}`)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[13px] font-ui font-medium text-ink/70 underline underline-offset-2"
                            >
                              /school/{slug}
                            </a>
                          ))}
                        </div>
                      )}

                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[13px]">
                        <p><span className="text-ink/50">Events</span> · {row.event_count}</p>
                        <p><span className="text-ink/50">Event RSVPs</span> · {row.event_rsvp_count}</p>
                        <p><span className="text-ink/50">Schools</span> · {row.school_count}</p>
                        <p><span className="text-ink/50">Classes</span> · {row.class_count}</p>
                        <p><span className="text-ink/50">Class RSVPs</span> · {row.class_rsvp_count}</p>
                        <p><span className="text-ink/50">Followers</span> · {row.school_follower_count}</p>
                      </div>
                      <p className="text-[12px] text-ink/50">
                        Last event {formatWhen(row.last_event_at)}
                        {row.verified_host ? " · Verified host" : ""}
                        {row.hosts_paid ? " · Paid" : ""}
                        {row.onboarding_completed ? " · Onboarded" : ""}
                      </p>
                    </CardBody>
                  </Card>
                );
              })}
            </div>
          )}
        </section>

        <section className="space-y-3">
          <button
            type="button"
            className="flex w-full items-center justify-between gap-3 text-left"
            onClick={() => setVerificationOpen((v) => !v)}
            data-testid="toggle-verification"
          >
            <div>
              <h2 className="font-ui font-semibold text-[18px]">Host verification</h2>
              <p className="text-[13px] text-ink/55">Verified host, Instagram, and sponsorship.</p>
            </div>
            <span className="font-ui text-[13px] text-ink/55">{verificationOpen ? "Hide" : "Show"}</span>
          </button>

          {verificationOpen && (
            <div className="space-y-4">
              {organizers.length === 0 ? (
                <StickerCard className="p-6">
                  <p className="font-ui font-semibold text-[18px]">No accounts yet</p>
                </StickerCard>
              ) : (
                organizers.map((org) => (
                  <StickerCard key={org.id} className="p-5 space-y-3" data-testid={`admin-org-${org.id}`}>
                    <div className="flex flex-wrap justify-between gap-2">
                      <div>
                        <h2 className="font-ui font-semibold text-[17px] sm:text-[18px]">{org.name || org.id}</h2>
                        <p className="text-[13px] font-medium text-ink/55">{org.email || org.phone || "—"}</p>
                      </div>
                      {organizerInstagramUrl(org) && (
                        <a
                          href={organizerInstagramUrl(org)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-btn border border-black/[0.08] bg-white px-3 py-1.5 min-h-[44px] font-ui font-medium text-[13px] text-ink hover:bg-canvas transition-colors"
                        >
                          <Instagram className="w-4 h-4" /> Profile
                        </a>
                      )}
                    </div>
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={!!org.verified_host}
                        onChange={(e) => updateAccount(org.id, { verified_host: e.target.checked })}
                        disabled={acting === org.id}
                        data-testid={`verified-host-${org.id}`}
                      />
                      <span className="font-display font-bold text-[14px]">Verified host</span>
                    </label>
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={!!org.instagram_verified}
                        onChange={(e) => updateAccount(org.id, { instagram_verified: e.target.checked })}
                        disabled={acting === org.id}
                        data-testid={`verify-${org.id}`}
                      />
                      <span className="font-display font-bold text-[14px]">Instagram verified</span>
                    </label>
                    <label className="block">
                      <span className="font-display font-bold text-[12px] uppercase tracking-wider">Sponsored by verified host</span>
                      <select
                        className="field mt-2"
                        value={org.sponsored_by_organizer_id || ""}
                        onChange={(e) =>
                          updateAccount(org.id, {
                            sponsored_by_organizer_id: e.target.value || null,
                          })
                        }
                        disabled={acting === org.id}
                        data-testid={`sponsor-${org.id}`}
                      >
                        <option value="">None</option>
                        {verifiedHosts
                          .filter((h) => h.id !== org.id)
                          .map((h) => (
                            <option key={h.id} value={h.id}>
                              {h.name}
                            </option>
                          ))}
                      </select>
                    </label>
                  </StickerCard>
                ))
              )}
            </div>
          )}
        </section>
      </div>

      <SwitchUserModal open={switchOpen} onClose={() => setSwitchOpen(false)} />
    </div>
  );
}
